import { FC, useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Option } from '../Option/Option';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { COLORS, device } from '../../../constants';
import { SHADOW } from '../../../constants/animations';
import { SEARCH_PLACEHOLDER } from '../../../constants/filters';

interface SelectListProps {
  optionsList: string[];
  isOpen: boolean;
  handleSelectClose: () => void;
  selectedOptions: string[];
  onOptionSelect: (option: string) => void;
  isMultiple: boolean;
  enableSearch?: boolean;
  maxHeight?: string;
  initialOption?: string;
}

export const SelectList: FC<SelectListProps> = ({
  optionsList,
  isOpen,
  handleSelectClose,
  selectedOptions,
  onOptionSelect,
  isMultiple,
  enableSearch = false,
  maxHeight,
  initialOption,
}) => {
  const listRef = useRef(null);
  useClickOutside(listRef, handleSelectClose, isOpen);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!enableSearch || !searchValue.trim()) {
      return optionsList;
    }

    const normalizedSearch = searchValue.toLowerCase();
    const optionsExcludingInitial = optionsList.filter(
      option => option !== initialOption
    );

    const matched = optionsExcludingInitial.filter(option =>
      option.toLowerCase().includes(normalizedSearch)
    );

    if (initialOption && optionsList.includes(initialOption)) {
      return [initialOption, ...matched];
    }
    return matched;
  }, [enableSearch, searchValue, optionsList, initialOption]);

  return (
    <SelectListContainer $isOpen={isOpen} ref={listRef}>
      {enableSearch && (
        <SearchWrapper>
          <SearchInput
            type="text"
            placeholder={SEARCH_PLACEHOLDER}
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
          />
        </SearchWrapper>
      )}
      <OptionsWrapper $maxHeight={maxHeight}>
        {filteredOptions.map((option, index) => (
          <Option
            key={`${option}-${index}`}
            text={option}
            isSelected={selectedOptions.includes(option)}
            onClick={() => onOptionSelect(option)}
            isMultiple={isMultiple}
          />
        ))}
      </OptionsWrapper>
    </SelectListContainer>
  );
};

const SelectListContainer = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  position: absolute;
  top: 52px;
  left: 0;
  right: 0;
  z-index: 30;
  background-color: ${COLORS.RED_PRESSED};
  box-shadow: ${SHADOW.HOVER.FILTER};
  box-sizing: border-box;
  border-top: none;
`;

const SearchWrapper = styled.div`
  padding: 7px 2px;
  background-color: ${COLORS.WHITE};
  border: 1px solid ${COLORS.INDUSTRY_BORDER};
  border-top: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  color: ${COLORS.SECTION_TEXT};
  font-size: 16px;
  box-sizing: border-box;
  border: none;
  outline: none;

  @media screen and ${device.md} {
    font-size: 14px;
    padding: 6px 6px;
  }
`;

const OptionsWrapper = styled.div<{ $maxHeight?: string }>`
  max-height: ${({ $maxHeight }) => $maxHeight || 'none'};
  overflow-y: ${({ $maxHeight }) => ($maxHeight ? 'auto' : 'visible')};
  background-color: #fff;
  overscroll-behavior: none;
`;
